<template>
  <div>
    <div class="tw-mt-6 tw-flex tw-flex-wrap tw-gap-4 tw-items-end">
      <v-autocomplete
        v-model="selectedYear"
        :items="years"
        label="Year"
        variant="outlined"
        density="comfortable"
        hide-details="auto"
        clearable
        class="tw-min-w-[140px]"
        :loading="loadingYears"
        @update:model-value="onYearChange"
      />
      <v-autocomplete
        v-model="selectedMake"
        :items="divisionItems"
        item-title="title"
        :return-object="true"
        label="Make"
        variant="outlined"
        density="comfortable"
        hide-details="auto"
        clearable
        class="tw-min-w-[200px]"
        :disabled="!selectedYear"
        :loading="loadingDivisions"
        @update:model-value="onMakeChange"
      />
      <v-autocomplete
        v-model="selectedModel"
        :items="modelItems"
        item-title="title"
        :return-object="true"
        label="Model"
        variant="outlined"
        density="comfortable"
        hide-details="auto"
        clearable
        class="tw-min-w-[200px]"
        :disabled="!selectedYear || !selectedMake"
        :loading="loadingModels"
      />
      <div class="tw-flex tw-gap-2 tw-items-end">
        <v-btn
          color="primary"
          variant="flat"
          height="48"
          :disabled="!canLoad"
          :loading="loading"
          @click="load"
        >
          Load
        </v-btn>
        <v-btn
          color="primary"
          variant="outlined"
          height="48"
          :disabled="!canSave"
          :loading="saving"
          @click="save"
        >
          Save
        </v-btn>
      </div>
    </div>

    <div v-if="!jazelAccountId" class="tw-mt-4 tw-text-sm tw-text-amber-800">
      No Jazel account id in account settings. Sign in or select an account in the app header.
    </div>

    <div v-if="error" class="tw-mt-4 tw-text-red-600 tw-text-sm">
      {{ error }}
    </div>
    <div v-if="saveError" class="tw-mt-2 tw-text-red-600 tw-text-sm">
      {{ saveError }}
    </div>
    <div v-if="saveMessage" class="tw-mt-2 tw-text-sm tw-text-green-800">
      {{ saveMessage }}
    </div>

    <div v-if="!error" class="tw-mt-6">
      <div v-if="!hasSearched" class="tw-text-sm tw-text-gray-600">
        Select year, make, and model, then click Load.
      </div>
      <div v-else-if="!loading && flatColorList.length === 0" class="tw-text-sm tw-text-gray-600">
        No missing trims found.
      </div>

      <div v-else-if="flatColorList.length > 0">
        <div v-for="(g, gi) in displayGroups" :key="g.groupKey" class="tw-mb-4">
          <v-row class="tw-mx-0">
            <v-col cols="12" class="tw-px-0 tw-py-0">
              <div class="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
                <div
                  class="group-move-stack tw-flex tw-shrink-0 tw-flex-col tw-items-stretch tw-rounded tw-bg-white/95 tw-p-0 tw-leading-none tw-shadow-sm"
                >
                  <v-btn
                    type="button"
                    icon
                    variant="text"
                    size="x-small"
                    density="compact"
                    class="group-move-btn tw-text-gray-600"
                    :disabled="!canMoveGroupUp(gi)"
                    aria-label="Move trim group up"
                    @click="moveGroupUp(gi)"
                  >
                    <v-icon
                      class="tw-m-0 tw-p-0 tw-leading-none"
                      icon="fa fa-arrow-up"
                      size="x-small"
                    />
                  </v-btn>
                  <v-btn
                    type="button"
                    icon
                    variant="text"
                    size="x-small"
                    density="compact"
                    class="group-move-btn tw-text-gray-600"
                    :disabled="!canMoveGroupDown(gi)"
                    aria-label="Move trim group down"
                    @click="moveGroupDown(gi)"
                  >
                    <v-icon
                      class="tw-m-0 tw-p-0 tw-leading-none"
                      icon="fa fa-arrow-down"
                      size="x-small"
                    />
                  </v-btn>
                </div>
                <div
                  class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-wrap tw-items-baseline tw-gap-x-2 tw-gap-y-0 tw-leading-tight"
                >
                  <span class="tw-text-xs tw-font-semibold tw-text-gray-900">{{ g.trimName }}</span>
                  <span class="tw-text-[11px] tw-tabular-nums tw-text-gray-600">
                    Base MSRP {{ formatPrice(g.baseMsrp) }}
                  </span>
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row class="tw-mx-0 tw-mt-1 tw-gap-y-1.5">
            <v-col
              v-for="slot in g.slots"
              :key="slot.flatIndex"
              cols="12"
              class="missing-trim-color-col"
            >
              <MissingTrimColorCard
                :color="slot.color"
                :base-msrp="slot.baseMsrp"
                :can-move-up="canMoveFlatUp(slot.flatIndex)"
                :can-move-down="canMoveFlatDown(slot.flatIndex)"
                @move-up="moveFlatUp(slot.flatIndex)"
                @move-down="moveFlatDown(slot.flatIndex)"
              />
            </v-col>
          </v-row>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRaw } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { getDivisions, getMissingTrims, getModels, getYears } from '@/api/entities/car';
import { getTrimOrders, patchTrimOrders, type TrimOrderItem } from '@/api/trimOrders';
import MissingTrimColorCard from '@/components/missingTrims/MissingTrimColorCard.vue';
import { useAccountStore } from '@/stores/accountStore';
import type { MissingTrimColor } from '@/components/missingTrims/MissingTrimColorCard.vue';

type DivisionItem = { title: string; value: { id: number; name: string } };
type ModelItem = { title: string; value: { id: number; name: string } };

interface MissingTrimRow {
  id: string;
  name: string;
  baseMsrp: number | null;
  colors: MissingTrimColor[];
}

/** Single-level row: one color plus its trim metadata (for grouping after reorder). */
interface FlatColorItem {
  trimId: string;
  trimName: string;
  baseMsrp: number | null;
  color: MissingTrimColor;
  /** Lexicographic ordering key (string compare / localeCompare). */
  sortKey: string;
}

/** One visual trim section: consecutive flat indices sharing the same trimId. */
interface TrimColorGroup {
  groupKey: string;
  trimId: string;
  trimName: string;
  baseMsrp: number | null;
  slots: { flatIndex: number; color: MissingTrimColor; baseMsrp: number | null }[];
}

function makeLexSortKey(trimId: string, trimName: string, color: MissingTrimColor): string {
  const parts = [
    trimId,
    trimName,
    color.name,
    color.chromeOptionCode ?? '',
    color.msrp != null && Number.isFinite(Number(color.msrp)) ? String(color.msrp) : '',
    color.primaryRGBHexCode ?? '',
  ];
  return parts.join('\u0001');
}

function lexCompare(a: string, b: string) {
  return a.localeCompare(b, undefined, { sensitivity: 'variant', numeric: true });
}

/** Flatten all colors into one array (then sort lexicographically by sortKey). */
function buildFlatColorListFromTrimRows(items: MissingTrimRow[]): FlatColorItem[] {
  const flat: FlatColorItem[] = [];
  for (const row of items) {
    for (const c of row.colors) {
      flat.push({
        trimId: row.id,
        trimName: row.name,
        baseMsrp: row.baseMsrp,
        color: c,
        sortKey: makeLexSortKey(row.id, row.name, c),
      });
    }
  }
  flat.sort((a, b) => lexCompare(a.sortKey, b.sortKey));
  return flat;
}

function normColorName(s: string) {
  return s.trim().toLowerCase();
}

function flatColorKey(item: FlatColorItem) {
  return `${item.trimId}\u0001${normColorName(item.color.name)}`;
}

function savedOrderKey(s: Pick<TrimOrderItem, 'trimId' | 'colorName'>) {
  return `${s.trimId}\u0001${normColorName(s.colorName)}`;
}

/** Reorder `flat` to match saved trim order; unknown rows keep their relative order at the end. */
function applySavedOrder(flat: FlatColorItem[], saved: TrimOrderItem[]): FlatColorItem[] {
  if (!saved.length) return flat;

  const byKey = new Map<string, FlatColorItem>();
  for (const item of flat) {
    byKey.set(flatColorKey(item), item);
  }

  const sortedSaved = [...saved].sort((a, b) => {
    const na = Number(a.sortKey);
    const nb = Number(b.sortKey);
    if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
    return a.sortKey.localeCompare(b.sortKey, undefined, { numeric: true });
  });

  const out: FlatColorItem[] = [];
  const used = new Set<string>();
  for (const s of sortedSaved) {
    const k = savedOrderKey(s);
    const item = byKey.get(k);
    if (item && !used.has(k)) {
      out.push(item);
      used.add(k);
    }
  }
  for (const item of flat) {
    const k = flatColorKey(item);
    if (!used.has(k)) out.push(item);
  }
  return out;
}

function assignSequentialSortKeys(items: FlatColorItem[]): FlatColorItem[] {
  return items.map((item, i) => ({
    ...item,
    sortKey: String(i + 1),
  }));
}

/**
 * Regroup flat list into trim sections: same trimId in a row → one group;
 * when trimId changes, start a new group.
 */
function groupColorsByTrim(flat: FlatColorItem[]): TrimColorGroup[] {
  const groups: TrimColorGroup[] = [];
  for (let i = 0; i < flat.length; i++) {
    const item = flat[i];
    const prev = groups[groups.length - 1];
    if (prev && prev.trimId === item.trimId) {
      prev.slots.push({
        flatIndex: i,
        color: item.color,
        baseMsrp: item.baseMsrp,
      });
    } else {
      groups.push({
        groupKey: `${item.trimId}\u0001${i}`,
        trimId: item.trimId,
        trimName: item.trimName,
        baseMsrp: item.baseMsrp,
        slots: [{ flatIndex: i, color: item.color, baseMsrp: item.baseMsrp }],
      });
    }
  }
  return groups;
}

const DEFAULT_YEAR = 2025;
const DEFAULT_MAKE = 'Ford';
const DEFAULT_MODEL = 'Bronco Sport';

const route = useRoute();
const accountStore = useAccountStore();
const { accountSettings } = storeToRefs(accountStore);

const jazelAccountId = computed(() => {
  const j = accountSettings.value?.juid;
  return typeof j === 'string' && j.trim() ? j.trim() : '';
});

const years = ref<number[]>([]);
const divisionItems = ref<DivisionItem[]>([]);
const modelItems = ref<ModelItem[]>([]);

const selectedYear = ref<number | null>(null);
const selectedMake = ref<DivisionItem | null>(null);
const selectedModel = ref<ModelItem | null>(null);

const loadingYears = ref(false);
const loadingDivisions = ref(false);
const loadingModels = ref(false);
const loading = ref(false);
const saving = ref(false);
const error = ref<string | null>(null);
const saveError = ref<string | null>(null);
const saveMessage = ref<string | null>(null);
const data = ref<null | { total: number; items: MissingTrimRow[] }>(null);
const hasSearched = ref(false);

/** Single-level list of colors (reorder by swapping indices); trim sections come from `groupColorsByTrim`. */
const flatColorList = ref<FlatColorItem[]>([]);

const displayGroups = computed(() => groupColorsByTrim(flatColorList.value));

/** Deep clone without structuredClone — API rows may be Vue/Pinia proxies, which structuredClone rejects. */
function cloneTrimItems(items: MissingTrimRow[] | undefined): MissingTrimRow[] {
  if (!items?.length) return [];
  return JSON.parse(JSON.stringify(toRaw(items))) as MissingTrimRow[];
}

function canMoveFlatUp(flatIndex: number) {
  return flatIndex > 0;
}

function canMoveFlatDown(flatIndex: number) {
  return flatIndex < flatColorList.value.length - 1;
}

function swapFlatAt(i: number, j: number) {
  const arr = flatColorList.value;
  if (j < 0 || j >= arr.length || i < 0 || i >= arr.length) return;
  const tmp = arr[i];
  arr[i] = arr[j];
  arr[j] = tmp;
}

function moveFlatUp(flatIndex: number) {
  swapFlatAt(flatIndex, flatIndex - 1);
}

function moveFlatDown(flatIndex: number) {
  swapFlatAt(flatIndex, flatIndex + 1);
}

function canMoveGroupUp(groupIdx: number) {
  return groupIdx > 0;
}

function canMoveGroupDown(groupIdx: number) {
  return groupIdx < groupColorsByTrim(flatColorList.value).length - 1;
}

/** Move one contiguous trim group (all its colors) before the previous group in the flat list. */
function moveGroupUp(groupIdx: number) {
  const groups = groupColorsByTrim(flatColorList.value);
  if (groupIdx <= 0) return;
  const g = groups[groupIdx];
  const prev = groups[groupIdx - 1];
  const start = g.slots[0].flatIndex;
  const end = g.slots[g.slots.length - 1].flatIndex;
  const prevStart = prev.slots[0].flatIndex;
  const len = end - start + 1;
  const arr = flatColorList.value;
  const block = arr.splice(start, len);
  arr.splice(prevStart, 0, ...block);
}

/** Move one contiguous trim group after the next group in the flat list. */
function moveGroupDown(groupIdx: number) {
  const groups = groupColorsByTrim(flatColorList.value);
  if (groupIdx >= groups.length - 1) return;
  const g = groups[groupIdx];
  const next = groups[groupIdx + 1];
  const start = g.slots[0].flatIndex;
  const end = g.slots[g.slots.length - 1].flatIndex;
  const nextEnd = next.slots[next.slots.length - 1].flatIndex;
  const len = end - start + 1;
  const arr = flatColorList.value;
  const block = arr.splice(start, len);
  const insertAt = nextEnd - len + 1;
  arr.splice(insertAt, 0, ...block);
}

const canLoad = computed(() =>
  Boolean(
    jazelAccountId.value &&
    selectedYear.value &&
    selectedMake.value?.value?.name &&
    selectedModel.value?.value?.name,
  ),
);

const canSave = computed(
  () => canLoad.value && flatColorList.value.length > 0 && !loading.value && !saving.value,
);

const formatPrice = (price: unknown) => {
  if (price == null) return '—';
  const n = Number(price);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString()}`;
};

const fetchYears = async () => {
  loadingYears.value = true;
  try {
    const res = await getYears();
    years.value = Array.isArray(res.data) ? res.data : [];
  } catch {
    years.value = [];
  } finally {
    loadingYears.value = false;
  }
};

const fetchDivisions = async () => {
  if (selectedYear.value == null) {
    divisionItems.value = [];
    return;
  }
  loadingDivisions.value = true;
  try {
    const res = await getDivisions(selectedYear.value);
    const raw = res.data as { id: number; name: string }[] | undefined;
    divisionItems.value = Array.isArray(raw)
      ? raw.map((d) => ({
          title: d.name,
          value: { id: d.id, name: d.name },
        }))
      : [];
  } catch {
    divisionItems.value = [];
  } finally {
    loadingDivisions.value = false;
  }
};

const fetchModelsList = async () => {
  if (selectedYear.value == null || !selectedMake.value) {
    modelItems.value = [];
    return;
  }
  loadingModels.value = true;
  try {
    const res = await getModels(selectedYear.value, selectedMake.value.value.id);
    const raw = res.data as { id: number; name: string }[] | undefined;
    modelItems.value = Array.isArray(raw)
      ? raw.map((m) => ({
          title: m.name,
          value: { id: m.id, name: m.name },
        }))
      : [];
  } catch {
    modelItems.value = [];
  } finally {
    loadingModels.value = false;
  }
};

const onYearChange = async () => {
  selectedMake.value = null;
  selectedModel.value = null;
  divisionItems.value = [];
  modelItems.value = [];
  await fetchDivisions();
};

const onMakeChange = async () => {
  selectedModel.value = null;
  modelItems.value = [];
  await fetchModelsList();
};

const normName = (s: string) => s.trim().toLowerCase();

const prefillTargets = () => {
  const q = route.query;
  const yearRaw = q.year != null && String(q.year) !== '' ? Number(q.year) : DEFAULT_YEAR;
  const year = Number.isFinite(yearRaw) ? yearRaw : DEFAULT_YEAR;
  const make = typeof q.make === 'string' && q.make.trim() ? q.make.trim() : DEFAULT_MAKE;
  const model = typeof q.model === 'string' && q.model.trim() ? q.model.trim() : DEFAULT_MODEL;
  return { year, make, model };
};

const applyPrefill = async () => {
  const { year, make, model } = prefillTargets();
  const y = years.value.includes(year) ? year : (years.value[0] ?? null);
  selectedYear.value = y;
  selectedMake.value = null;
  selectedModel.value = null;
  divisionItems.value = [];
  modelItems.value = [];
  if (y == null) return;
  await fetchDivisions();
  const makeItem = divisionItems.value.find((d) => normName(d.value.name) === normName(make));
  if (!makeItem) return;
  selectedMake.value = makeItem;
  await fetchModelsList();
  const modelItem = modelItems.value.find((m) => normName(m.value.name) === normName(model));
  if (modelItem) selectedModel.value = modelItem;
};

const load = async () => {
  if (!canLoad.value) return;
  loading.value = true;
  error.value = null;
  saveError.value = null;
  saveMessage.value = null;
  hasSearched.value = true;
  try {
    const res = await getMissingTrims({
      year: selectedYear.value!,
      make: selectedMake.value!.value.name,
      model: selectedModel.value!.value.name,
      accountId: jazelAccountId.value,
    });
    data.value = res.data ?? null;
    const cloned = cloneTrimItems(data.value?.items);
    let flat = buildFlatColorListFromTrimRows(cloned);
    try {
      const orders = await getTrimOrders(jazelAccountId.value, {
        year: selectedYear.value!,
        make: selectedMake.value!.value.name,
        model: selectedModel.value!.value.name,
      });
      if (orders.trimOrders?.length) {
        flat = applySavedOrder(flat, orders.trimOrders);
      }
    } catch {
      /* no saved order yet or API error — keep lex order */
    }
    flatColorList.value = assignSequentialSortKeys(flat);
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } }; message?: string };
    error.value = err?.response?.data?.message ?? err?.message ?? 'Failed to load';
    data.value = null;
    flatColorList.value = [];
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  if (!canSave.value) return;
  saving.value = true;
  saveError.value = null;
  saveMessage.value = null;
  try {
    const trimOrders = flatColorList.value.map((item, i) => ({
      trimId: item.trimId,
      colorName: item.color.name,
      sortKey: String(i + 1),
    }));
    await patchTrimOrders(jazelAccountId.value, {
      year: selectedYear.value!,
      make: selectedMake.value!.value.name,
      model: selectedModel.value!.value.name,
      trimOrders,
    });
    saveMessage.value = 'Order saved.';
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } }; message?: string };
    saveError.value = err?.response?.data?.message ?? err?.message ?? 'Failed to save';
  } finally {
    saving.value = false;
  }
};

onMounted(async () => {
  await fetchYears();
  await applyPrefill();
});
</script>

<style scoped>
/* Match MissingTrimColorCard arrow-stack sizing */
.group-move-btn {
  min-height: 0 !important;
  min-width: 0 !important;
  height: 14px !important;
  width: 14px !important;
  padding: 0 !important;
  margin: 0 !important;
  border-radius: 2px;
}
.group-move-btn + .group-move-btn {
  margin-top: 0 !important;
}
.group-move-stack :deep(.v-btn__content) {
  padding: 0 !important;
  line-height: 1 !important;
}
.group-move-stack :deep(.v-btn__overlay) {
  border-radius: 2px;
}

/* 1 → 2 → 3 → 4 cards per row */
.missing-trim-color-col {
  flex: 0 0 100%;
  max-width: 100%;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}
@media (min-width: 600px) {
  .missing-trim-color-col {
    flex: 0 0 50%;
    max-width: 50%;
  }
}
@media (min-width: 960px) {
  .missing-trim-color-col {
    flex: 0 0 33.333333%;
    max-width: 33.333333%;
  }
}
@media (min-width: 1280px) {
  .missing-trim-color-col {
    flex: 0 0 25%;
    max-width: 25%;
  }
}
</style>
